/**
 * Hook for label generation and printing.
 *
 * Each print function auto-fires the offline print notification when QZ Tray
 * is not connected or no printer is selected. Pass `{ silent: true }` to
 * suppress the notification (e.g. when wrapping prints in a bulk queue that
 * shows its own end-of-run prompt).
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
// @ts-ignore
import qz from 'qz-tray';
import { selectShipmentId, selectShipmentRouteSegments } from '../../../redux/packing';
import { usePrinterConnection } from './usePrinterConnection';
import { useOfflinePrintNotification } from './useOfflinePrintNotification';
// Helper to check if QZ websocket is actually active
var isQzWebsocketActive = function () {
    return qz && qz.websocket && qz.websocket.isActive();
};
/**
 * Open a label URL in a new tab. Pure function (no React state) so it can be
 * imported safely from useOfflinePrintNotification without a circular hook
 * dependency.
 */
export function downloadLabelUrl(url) {
    var moquiSessionToken = localStorage.getItem('moquiSessionToken');
    var downloadUrl = url + "&moquiSessionToken=" + moquiSessionToken;
    var link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
export var useLabelPrinting = function () {
    var shipmentId = useSelector(selectShipmentId);
    var shipmentRouteSegments = useSelector(selectShipmentRouteSegments);
    var _a = usePrinterConnection(), resolvePrinterForPrint = _a.resolvePrinterForPrint, printLabel = _a.printLabel;
    var promptIfOffline = useOfflinePrintNotification().promptIfOffline;
    var getLabelUrl = useCallback(function (type, packageSeqId, containerId) {
        var baseUrl = process.env.REACT_APP_API_BASE_URL;
        var joinIds = function (ids) {
            if (Array.isArray(ids))
                return ids.filter(Boolean).map(encodeURIComponent).join(',');
            return ids ? encodeURIComponent(ids) : '';
        };
        var pkgQs = joinIds(packageSeqId);
        var containerQs = joinIds(containerId);
        switch (type) {
            case 'main':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentAllLabel?shipmentId=" + shipmentId + "&shipmentPackageSeqId=" + pkgQs;
            case 'return':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentPackageReturnLabel?shipmentId=" + shipmentId + "&shipmentPackageSeqId=" + pkgQs;
            case 'all':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentAllLabel?shipmentId=" + shipmentId + (pkgQs ? "&shipmentPackageSeqId=" + pkgQs : '');
            case 'containerLabel':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentContainerLicensePlate?shipmentId=" + shipmentId + (containerQs ? "&containerId=" + containerQs : '');
            case 'containerInsert':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentContainerInsert?shipmentId=" + shipmentId + (pkgQs ? "&shipmentPackageSeqId=" + pkgQs : '');
            case 'packingSlip':
                return baseUrl + "/fop/apps/PopcAdmin/Shipping/ShipmentInsert?shipmentId=" + shipmentId + (pkgQs ? "&shipmentPackageSeqId=" + pkgQs : '');
            case 'freightPackingList':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/exportFreightPackingListXLSX?shipmentId=" + shipmentId;
            case 'billOfLading':
                return baseUrl + "/fop/apps/PopcAdmin/Shipment/ShipmentBillOfLading?shipmentId=" + shipmentId;
            default:
                return '';
        }
    }, [shipmentId]);
    var downloadLabel = useCallback(function (url) { return downloadLabelUrl(url); }, []);
    var runPrint = useCallback(function (labelUrl, printerType, options) {
        if (options === void 0) { options = {}; }
        return __awaiter(void 0, void 0, Promise, function () {
            var printerName, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, resolvePrinterForPrint(printerType, {
                            refresh: options.refreshPrinter || !isQzWebsocketActive(),
                            showWarning: false
                        })];
                    case 1:
                        printerName = _a.sent();
                        if (!isQzWebsocketActive()) {
                            return [2 /*return*/, { status: 'skipped', reason: 'qz-disconnected', labelUrl: labelUrl, printerType: printerType }];
                        }
                        if (!printerName) {
                            return [2 /*return*/, { status: 'skipped', reason: 'no-printer-selected', labelUrl: labelUrl, printerType: printerType }];
                        }
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, Promise.race([
                                printLabel(labelUrl, printerType, printerName),
                                new Promise(function (_, reject) {
                                    return setTimeout(function () { return reject(new Error('Print timed out after 30 seconds')); }, 30000);
                                })
                            ])];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, { status: 'printed' }];
                    case 4:
                        error_1 = _a.sent();
                        return [2 /*return*/, { status: 'error', error: error_1, labelUrl: labelUrl, printerType: printerType }];
                    case 5: return [2 /*return*/];
                }
            });
        });
    }, [resolvePrinterForPrint, printLabel]);
    var tryPrint = useCallback(function (labelUrl, printerType, opts) { return __awaiter(void 0, void 0, Promise, function () {
        var outcome;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, runPrint(labelUrl, printerType)];
                case 1:
                    outcome = _a.sent();
                    if (!(opts === null || opts === void 0 ? void 0 : opts.silent)) {
                        promptIfOffline(outcome, {
                            context: opts === null || opts === void 0 ? void 0 : opts.context,
                            onRetry: function () { return runPrint(labelUrl, printerType, { refreshPrinter: true }); }
                        });
                    }
                    return [2 /*return*/, outcome];
            }
        });
    }); }, [runPrint, promptIfOffline]);
    var printMainLabel = useCallback(function (shipmentPackageSeqId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('main', shipmentPackageSeqId), 'main', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    var printReturnLabel = useCallback(function (shipmentPackageSeqId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        var shipmentRouteSegmentSeqId, labelUrl;
        var _a;
        return __generator(this, function (_b) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            shipmentRouteSegmentSeqId = (_a = shipmentRouteSegments === null || shipmentRouteSegments === void 0 ? void 0 : shipmentRouteSegments[0]) === null || _a === void 0 ? void 0 : _a.shipmentRouteSegmentSeqId;
            labelUrl = process.env.REACT_APP_API_BASE_URL + "/rest/s1/handheld/pack/shipment/" + shipmentId + "/label?shipmentRouteSegmentSeqId=" + shipmentRouteSegmentSeqId + "&shipmentPackageSeqId=" + shipmentPackageSeqId + "&retLabel=true";
            return [2 /*return*/, tryPrint(labelUrl, 'main', opts)];
        });
    }); }, [shipmentId, shipmentRouteSegments, tryPrint]);
    var printAllLabels = useCallback(function (shipmentPackageSeqId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('all', shipmentPackageSeqId), 'main', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    var printPackingSlip = useCallback(function (shipmentPackageSeqId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('packingSlip', shipmentPackageSeqId), 'main', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    var printContainerInsert = useCallback(function (shipmentPackageSeqId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('containerInsert', shipmentPackageSeqId), 'main', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    /**
     * SPS LABEL
     */
    var printCartonLabel = useCallback(function (productStoreId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        var moquiSessionToken, response, responseData, url, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!shipmentId)
                        return [2 /*return*/, { status: 'printed' }];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    moquiSessionToken = localStorage.getItem('moquiSessionToken');
                    return [4 /*yield*/, axios.get("/rest/s1/handheld/pack/shipment/" + shipmentId + "/spsCartonLabel?shipmentId=" + shipmentId + "&productStoreId=" + productStoreId, { headers: { moquiSessionToken: moquiSessionToken || '' } })];
                case 2:
                    response = _a.sent();
                    responseData = response.data;
                    if (response.status === 200 && (responseData === null || responseData === void 0 ? void 0 : responseData.shipmentContentId)) {
                        url = process.env.REACT_APP_API_BASE_URL + "/apps/oms/Shipment/ShipmentDetail/downloadContent?shipmentId=" + shipmentId + "&shipmentContentId=" + responseData.shipmentContentId;
                        return [2 /*return*/, tryPrint(url, 'label', opts)];
                    }
                    return [2 /*return*/, { status: 'error', error: new Error('No shipmentContentId returned'), labelUrl: '', printerType: 'label' }];
                case 3:
                    error_2 = _a.sent();
                    console.error('Error printing carton label:', error_2);
                    return [2 /*return*/, { status: 'error', error: error_2, labelUrl: '', printerType: 'label' }];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [shipmentId, tryPrint]);
    var printContainerLabel = useCallback(function (containerId, opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('containerLabel', undefined, containerId), 'label', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    var printBillOfLading = useCallback(function (opts) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!shipmentId)
                return [2 /*return*/, { status: 'printed' }];
            return [2 /*return*/, tryPrint(getLabelUrl('billOfLading'), 'main', opts)];
        });
    }); }, [shipmentId, getLabelUrl, tryPrint]);
    return {
        printMainLabel: printMainLabel,
        printReturnLabel: printReturnLabel,
        printAllLabels: printAllLabels,
        printPackingSlip: printPackingSlip,
        printContainerInsert: printContainerInsert,
        printCartonLabel: printCartonLabel,
        printContainerLabel: printContainerLabel,
        printBillOfLading: printBillOfLading,
        downloadLabel: downloadLabel,
        getLabelUrl: getLabelUrl
    };
};
export default useLabelPrinting;
